import { useCallback } from 'react';
import { useCanvas } from '../contexts/CanvasContext';
import { useHistoryStore } from '../stores/historyStore';

// Import fabric for side effects
import 'fabric';

export const useHistory = () => {
  const { canvas } = useCanvas();
  const {
    history,
    historyIndex,
    saveSnapshot,
    undo: undoStore,
    redo: redoStore,
    canUndo: canUndoFn,
    canRedo: canRedoFn,
    clear,
    setRestoring,
  } = useHistoryStore();

  const canUndo = canUndoFn();
  const canRedo = canRedoFn();

  // Save current canvas state to history
  const saveState = useCallback(() => {
    if (!canvas) return;

    const state = JSON.stringify(
      canvas.toJSON(['id', 'isGuide', 'isMockup', 'isBaseImage', 'isMaskOverlay', 'uploadedUrl', 'serverUrl', 'laserConverted', 'convertedFrom'])
    );
    saveSnapshot(state);
  }, [canvas, saveSnapshot]);

  // Restore canvas from a JSON state string
  const restoreState = useCallback((stateJson: string) => {
    if (!canvas) return;

    canvas.loadFromJSON(stateJson, () => {
      canvas.renderAll();

      // Re-apply any system object protections
      canvas.getObjects().forEach((obj) => {
        if (obj.isGuide || obj.isMockup || obj.isBaseImage || obj.isMaskOverlay) {
          obj.set({
            selectable: false,
            evented: false,
          });
        }
      });
    });
  }, [canvas]);

  // Undo operation
  const undo = useCallback(() => {
    if (!canUndo || !canvas) return;

    setRestoring(true);
    const previousState = undoStore();
    if (previousState) {
      restoreState(previousState);
    }
    setRestoring(false);
  }, [canvas, canUndo, undoStore, restoreState, setRestoring]);

  // Redo operation
  const redo = useCallback(() => {
    if (!canRedo || !canvas) return;

    setRestoring(true);
    const nextState = redoStore();
    if (nextState) {
      restoreState(nextState);
    }
    setRestoring(false);
  }, [canvas, canRedo, redoStore, restoreState, setRestoring]);

  // Clear history (useful when loading a new design)
  const clearHistory = useCallback(() => {
    clear();
  }, [clear]);

  return {
    saveState,
    undo,
    redo,
    canUndo,
    canRedo,
    clearHistory,
    historyLength: history.length,
    currentIndex: historyIndex,
  };
};

export default useHistory;
